import React, { useEffect, useState } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";

/**
 * BatchCover — renders a batch's cover asset (image OR video).
 *
 * UX (post user feedback round 3):
 *
 *   "As soon as the student lands on the page, the thumbnail of the video
 *    should already be visible, and after 2–3 seconds it should automatically
 *    auto-play in muted mode. After that, it should be up to the student
 *    whether they want to unmute it or not."
 *
 * Implementation:
 *
 *   1. Poster paints INSTANTLY (cover_image > YouTube maxresdefault > hqdefault).
 *      Bunny libraries are token-protected and don't expose a public thumbnail,
 *      so when the cover is a Bunny video the admin should also upload a
 *      cover_image; we surface a clean dark backdrop + cinematic "Watch intro"
 *      affordance otherwise.
 *
 *   2. After 2.2s (gives the page a moment to settle, prevents jank during
 *      hero animations) we mount the iframe with autoplay=1 + mute=1. Browsers
 *      always allow muted autoplay — no user gesture required.
 *
 *   3. A beautiful glass-morphic "Unmute" pill sits bottom-right while muted.
 *      Click it → reload iframe with mute=0 + a JS-driven user gesture so the
 *      audio kicks in cleanly. Also adds a "Mute" toggle once unmuted so the
 *      student can reverse course.
 *
 *   4. Container forces aspectRatio: 16/9 inline so YouTube pillarboxes
 *      vertical sources instead of cropping them — no edge-cutoff anywhere.
 *
 *   5. `interactive={false}` mode (used inside outer <button>/<a> wrappers
 *      like the App.js batch-picker) skips the iframe entirely and only
 *      renders the poster + a small play-badge affordance, keeping HTML
 *      semantics valid (no nested clickables).
 */
export default function BatchCover({
  batch,
  imgClassName = "absolute inset-0 w-full h-full object-cover",
  onError,
  className = "",
  alt,
  showWatchChip = true,
  interactive = true,
  // Delay before muted autoplay kicks in. 2.2s is the sweet-spot — long
  // enough that hero animations don't compete with the iframe spinning up,
  // short enough that the student notices the video starts on its own.
  autoplayDelayMs = 2200,
  // eslint-disable-next-line no-unused-vars
  ...rest
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false); // hides the <img> tag after 404 — no browser "broken" glyph
  const [posterFallback, setPosterFallback] = useState(false);
  // `mounted` controls whether the iframe is in the DOM at all. We keep
  // the poster painted until just before mount, then layer the iframe on
  // top so the transition is invisible.
  const [mounted, setMounted] = useState(false);
  const [muted, setMuted] = useState(true);
  // Bumping `playerKey` re-mounts the iframe (used when toggling sound — we
  // don't rely on YouTube postMessage to avoid IFrame-API JS bloat).
  const [playerKey, setPlayerKey] = useState(0);

  const ytId = batch?.cover_video_source === "youtube" ? extractYouTubeId(batch?.cover_video_url) : null;
  const bunnyId = batch?.cover_video_source === "bunny" ? batch?.cover_video_id : null;
  const hasVideo = !!(ytId || bunnyId);
  const hasImage = !!batch?.cover_image && !imgFailed;

  // Schedule auto-mount on the configured delay (interactive-only — small
  // thumbs in non-interactive mode skip the iframe entirely). MUST be
  // declared before any conditional return to satisfy React's
  // rules-of-hooks (useEffect always runs in the same order).
  useEffect(() => {
    if (!interactive || !hasVideo) return undefined;
    const t = setTimeout(() => setMounted(true), autoplayDelayMs);
    return () => clearTimeout(t);
  }, [interactive, hasVideo, autoplayDelayMs]);

  if (!batch) return null;
  if (!hasVideo && !hasImage) return null;

  /* ---------------- IMAGE-ONLY PATH ---------------- */
  if (!hasVideo) {
    return (
      <img
        src={batch.cover_image}
        alt={alt || batch.name || "Cover image"}
        className={imgClassName}
        onError={(e) => {
          setImgFailed(true);
          onError?.(e);
        }}
      />
    );
  }

  /* ---------------- VIDEO PATH ---------------- */
  const BUNNY_LIBRARY_ID = batch?.cover_video_library_id || process.env.REACT_APP_BUNNY_LIBRARY_ID;
  const ytMaxres = ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : null;
  const ytHq = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;
  // Bunny stream thumbnail — backend pre-computes the CDN URL into
  // `cover_video_thumbnail_url`. When the admin uploads a cover_image, that
  // wins (custom always > auto-generated). This fallback prevents the empty
  // black box on the login-modal batch-picker for batches with Bunny videos
  // but no custom cover image.
  const customPoster = batch.cover_image && !imgFailed ? batch.cover_image : null;
  const poster = customPoster
    || (ytId ? (posterFallback ? ytHq : ytMaxres) : null)
    || (bunnyId ? batch.cover_video_thumbnail_url : null);

  // Build the iframe src with the current mute state. Bumping `playerKey`
  // remounts the iframe so the new query string is picked up immediately.
  const muteFlag = muted ? "1" : "0";
  const bunnyMuteFlag = muted ? "true" : "false";
  const baseBunny = batch.cover_video_embed_url
    ? `${batch.cover_video_embed_url}${batch.cover_video_embed_url.includes("?") ? "&" : "?"}autoplay=true&muted=${bunnyMuteFlag}&preload=true&responsive=true&loop=true`
    : BUNNY_LIBRARY_ID && bunnyId
      ? `https://iframe.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${bunnyId}?autoplay=true&muted=${bunnyMuteFlag}&preload=true&responsive=true&loop=true`
      : null;
  const src = ytId
    ? `https://www.youtube.com/embed/${ytId}?autoplay=1&mute=${muteFlag}&loop=1&playlist=${ytId}&controls=1&rel=0&modestbranding=1&iv_load_policy=3&playsinline=1`
    : baseBunny;

  return (
    <div
      className={`absolute inset-0 w-full h-full overflow-hidden bg-[#061226] ${className}`}
      data-batch-cover-mode={mounted ? "video" : "poster"}
      style={{ aspectRatio: "16 / 9" }}
      aria-label={alt || batch.name || "Cover video"}
    >
      {/* POSTER — painted first, kept underneath the iframe forever as a
          background plate so any momentary load gap stays beautiful.
          Hidden entirely when the image 404s so the browser's broken-image
          glyph never leaks through. */}
      {poster && !posterFailed && (
        <img
          src={poster}
          alt={alt || batch.name || ""}
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => {
            // Try YouTube hqdefault as a first fallback when maxres 404s
            // (YouTube doesn't generate maxres for all videos).
            if (!posterFallback && ytId && !customPoster) {
              setPosterFallback(true);
              return;
            }
            // All poster candidates exhausted — hide the <img> so the clean
            // branded fallback (gradient + play icon + batch title) shows.
            setPosterFailed(true);
            setImgFailed(true);
            if (typeof onError === "function") onError();
          }}
        />
      )}

      {/* Branded fallback — when there's a video but no working poster
          (Bunny token-auth blocks thumbnail.jpg, cover_image not uploaded),
          show a quiet gradient backdrop. We deliberately DON'T render any
          "Batch preview" / batch-name caption here: the iframe mounts
          milliseconds later anyway and the caption was perceived as visual
          clutter blocking the actual video poster underneath. */}
      {(!poster || posterFailed) && !mounted && (
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, #0b1e4a 0%, #061226 55%, #0a1b3a 100%)",
          }}
          aria-hidden
        />
      )}

      {/* Cinematic scrim — soft radial vignette + edge fades for legibility
          of the chip + unmute button. Always-on, low intensity so the
          poster shines through. */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-700"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
          opacity: mounted ? 0.55 : 1,
        }}
      />
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/45 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />

      {/* IFRAME — mounts after the autoplay delay and starts muted. The
          poster stays underneath as a plate so any spinner-frame is hidden. */}
      {interactive && mounted && src && (
        <iframe
          key={`bc-iframe-${playerKey}-${muted ? "m" : "u"}`}
          src={src}
          title={batch.name || "Batch cover video"}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          style={{ border: 0, display: "block", background: "transparent" }}
        />
      )}

      {/* Big cinematic play affordance — only shown during the brief poster
          window before autoplay kicks in (and on non-interactive thumbs).
          Lets students know "this is a video, hang tight". */}
      {(!mounted || !interactive) && showWatchChip && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {interactive ? (
            <div className="relative">
              <span
                className="absolute -inset-3 rounded-full opacity-50 animate-ping"
                style={{
                  background:
                    "radial-gradient(circle, rgba(0,229,255,0.45) 0%, rgba(0,229,255,0) 70%)",
                }}
                aria-hidden="true"
              />
              <span
                className="absolute -inset-1 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(0,229,255,0.45) 0%, rgba(0,229,255,0) 75%)",
                  filter: "blur(10px)",
                }}
                aria-hidden="true"
              />
              <span
                className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #5ceeff 0%, #00d4f5 50%, #00b8d9 100%)",
                  boxShadow:
                    "0 12px 36px rgba(0,229,255,0.45), 0 0 0 1px rgba(255,255,255,0.35) inset",
                }}
              >
                <Play
                  size={20}
                  className="text-[#061226] translate-x-[2px]"
                  fill="currentColor"
                  strokeWidth={0}
                />
              </span>
            </div>
          ) : (
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full"
              style={{
                background: "linear-gradient(135deg, #5ceeff 0%, #00d4f5 100%)",
                boxShadow:
                  "0 4px 14px rgba(0,229,255,0.45), 0 0 0 1px rgba(255,255,255,0.35) inset",
              }}
            >
              <Play
                size={12}
                className="text-[#061226] translate-x-[1px]"
                fill="currentColor"
                strokeWidth={0}
              />
            </span>
          )}
        </div>
      )}

      {/* "▶ WATCH INTRO" chip — bottom-right when video is queued / playing.
          Hidden in non-interactive (thumb) mode where space is tight. */}
      {showWatchChip && interactive && (
        <div
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full pointer-events-none transition-opacity duration-500"
          style={{
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.16)",
            opacity: mounted ? 0.85 : 1,
          }}
        >
          <span className="relative flex w-1.5 h-1.5">
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: "#ff4d4d" }}
            />
            <span
              className="relative w-1.5 h-1.5 rounded-full"
              style={{ background: "#ff4d4d" }}
            />
          </span>
          <span
            className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-white"
            style={{ fontFamily: "var(--font-mono, ui-monospace)" }}
          >
            Intro
          </span>
        </div>
      )}

      {/* MUTE / UNMUTE pill — only visible once the iframe is mounted. */}
      {interactive && mounted && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMuted((m) => !m);
            setPlayerKey((k) => k + 1);
          }}
          className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] font-semibold transition-all duration-300 hover:scale-105"
          style={{
            background: muted
              ? "linear-gradient(135deg, rgba(0,229,255,0.95) 0%, rgba(0,184,217,0.95) 100%)"
              : "rgba(0,0,0,0.6)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: muted
              ? "1px solid rgba(255,255,255,0.4)"
              : "1px solid rgba(255,255,255,0.18)",
            color: muted ? "#061226" : "#fff",
            fontFamily: "var(--font-mono, ui-monospace)",
            letterSpacing: "0.06em",
            boxShadow: muted
              ? "0 8px 24px rgba(0,229,255,0.35), 0 0 0 1px rgba(255,255,255,0.45) inset"
              : "0 4px 14px rgba(0,0,0,0.4)",
          }}
          aria-label={muted ? "Unmute video" : "Mute video"}
          data-testid={muted ? "batch-cover-unmute" : "batch-cover-mute"}
        >
          {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          {muted ? "Tap to unmute" : "Mute"}
        </button>
      )}
    </div>
  );
}

/* --------- helpers --------- */
export function extractYouTubeId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/
  );
  return match ? match[1] : null;
}

export function batchHasCoverVideo(batch) {
  if (!batch) return false;
  if (batch.cover_video_source === "youtube" && extractYouTubeId(batch.cover_video_url)) return true;
  if (batch.cover_video_source === "bunny" && batch.cover_video_id) return true;
  return false;
}
